/**
 * Real Evolu data source for Lightning transaction persistence.
 *
 * Implements LightningTransactionDataSource from useLightningTransactions,
 * writing to the Evolu transaction table. All Evolu branded type conversions
 * use the getOrThrow(Type.from(x)) pattern.
 *
 * SECURITY: Never logs paymentHash, bolt11, or preimage values.
 * These are sensitive payment data (bearer instruments for Lightning).
 *
 * Tier: 2 (Lightning features).
 *
 * @module lightning/hooks/useLightningTransactionsDataSource
 */

import { useCallback, useMemo } from 'react';
import { useEvolu } from '@evolu/react';
import { NonEmptyString1000, SqliteBoolean, getOrThrow } from '@evolu/common';
import type { Evolu } from '@evolu/common';
import type { SatsflowSchema } from '@/db/database';
import type {
  LightningTransactionDataSource,
  LightningTransactionRecord,
} from './useLightningTransactions';

/**
 * Cast the generic Evolu instance to the app's typed schema.
 *
 * @param evolu - Generic Evolu instance
 * @returns Typed Evolu instance
 */
function typedEvolu(evolu: ReturnType<typeof useEvolu>): Evolu<SatsflowSchema> {
  return evolu as unknown as Evolu<SatsflowSchema>;
}

/**
 * Convert a string to NonEmptyString1000 or return null if the string is null.
 *
 * @param value - String value or null
 * @returns Branded NonEmptyString1000 or null
 */
function toNullableString(value: string | null | undefined): NonEmptyString1000 | null {
  if (value === null || value === undefined || value === '') return null;
  return getOrThrow(NonEmptyString1000.from(value)) as NonEmptyString1000;
}

/** Return type for useLightningTransactionsDataSource. */
export interface UseLightningTransactionsDataSourceResult {
  /** The real Evolu-backed data source for Lightning transactions. */
  readonly dataSource: LightningTransactionDataSource;
}

/**
 * Provide a real Evolu data source for Lightning transaction persistence.
 *
 * Creates transaction rows in the Evolu transaction table with all required
 * fields populated. Lightning-specific fields (paymentHash, bolt11, preimage,
 * lightningAddress) are nullable. Cashu-specific fields (mintId, cashuToken)
 * are always null for Lightning transactions.
 *
 * @returns Object with dataSource for useLightningTransactions
 */
export function useLightningTransactionsDataSource(): UseLightningTransactionsDataSourceResult {
  const evolu = useEvolu();

  const recordTransaction = useCallback(
    (data: LightningTransactionRecord): void => {
      const db = typedEvolu(evolu);

      db.insert('transaction', {
        type: getOrThrow(NonEmptyString1000.from(data.type)),
        direction: getOrThrow(NonEmptyString1000.from(data.direction)),
        status: getOrThrow(NonEmptyString1000.from(data.status)),
        amountSats: data.amountSats,
        feeSats: data.feeSats,
        paymentHash: toNullableString(data.paymentHash),
        bolt11: toNullableString(data.bolt11),
        bolt12Offer: null,
        preimage: toNullableString(data.preimage),
        lightningAddress: toNullableString(data.lightningAddress),
        note: toNullableString(data.note),
        mintId: null,
        cashuToken: null,
        contactId: data.contactId ? toNullableString(data.contactId) : null,
        categoryId: null,
        externalId: null,
        walletConnectionId: toNullableString(data.connectionId ?? null),
        initiatedAt: getOrThrow(NonEmptyString1000.from(data.initiatedAt)),
        settledAt: toNullableString(data.settledAt),
        expiresAt: null,
        custodyType: getOrThrow(NonEmptyString1000.from(data.custodyType)),
        exportedToSatsflow: getOrThrow(SqliteBoolean.from(0)),
        exportedToClams: getOrThrow(SqliteBoolean.from(0)),
      });
    },
    [evolu],
  );

  const tagTransaction = useCallback(
    (transactionId: string, connectionId: string, evoluId?: unknown): void => {
      const db = typedEvolu(evolu);
      // Use the original branded Evolu ID if provided, fall back to string
      db.update('transaction', {
        id: evoluId ?? transactionId,
        walletConnectionId: toNullableString(connectionId),
      } as never);
    },
    [evolu],
  );

  const dataSource: LightningTransactionDataSource = useMemo(
    () => ({ recordTransaction, tagTransaction }),
    [recordTransaction, tagTransaction],
  );

  return { dataSource };
}
